import React, { useState, useMemo, useEffect, useRef } from 'react';
import api from '../api/api';
import Layout from '../components/Layout';
import AccountDetailModal from '../components/AccountDetailModal';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPen, faTrash, faBuilding, faTimesCircle, faUser,
    faFileImport, faDownload, faCheck, faTimes, faUpload,
    faSearch, faCalendarAlt, faSortAmountDown, faSortAmountUp,
    faSyncAlt, faLayerGroup, faPlus
} from '@fortawesome/free-solid-svg-icons';

function Leads() {
    const { empId, roleId, teamMemberIds, isSuper, isManager, canDelete, getRbacParams, activeOrgId, canWrite } = useAuth();
    const [leads, setLeads] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // XLSX Import State
    const [importModal, setImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState([]);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileInputRef = useRef(null);

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null
    });

    const fetchLeads = async () => {
        try {
            setLoading(true);
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();
            const res = await api.get(`/leads?${qstr}`);

            // Map backend fields → clean frontend record
            const formatted = (res.data || []).map(item => ({
                id: item.AccountID,
                name: item.AccountName || '',
                type: item.AccountType || 'Organization',
                contactPerson: item.ContactPerson || '',
                designation: item.ContactDesignation || '',
                phone: item.CntPhone1 || '',
                email: item.CntMail1 || '',
                phone2: item.CntPhone2 || '',
                email2: item.CntMail2 || '',
                city: item.City || '',
                linkedin: item.Linkedin || '',
                status: item.Status || 'Lead',
                priority: item.Priority || 'Medium',
                approachStatus: item.ApproachStatus || 'New Lead',
                contactedAt: item.ContactedAt,
                contactMethod: item.ContactMethod,
                contactNotes: item.ContactNotes,
                assignedTo: item.AssignedTo,
                assignedToName: item.AssignedToName,
                leadGenerator: item.LeadGenerator,
                leadGeneratorName: item.LeadGeneratorName,
                responsible: item.Responsible,
                responsibleName: item.ResponsibleName,
                notes: item.Notes || '',
                createdAt: item.CreatedAt,
                updatedAt: item.UpdatedAt
            }));

            setLeads(formatted);
        } catch (err) {
            console.error('Failed to fetch leads:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();
            const res = await api.get(`/users?${qstr}`);
            setUsers(res.data || []);
        } catch (e) {
            console.error('Failed to fetch users:', e);
        }
    };

    // Subordinates + self only: strictly exclude any seniors (users with higher RoleID)
    const assignableLeadMembers = useMemo(() => {
        return users.filter(u => {
            if (roleId && Number(u.RoleID) > Number(roleId)) return false;
            if (isSuper) return true;
            if (isManager) {
                return (
                    Number(u.ReportingTo) === Number(empId) ||
                    Number(u.empId) === Number(empId) ||
                    (teamMemberIds && teamMemberIds.map(Number).includes(Number(u.empId)))
                );
            }
            return Number(u.empId) === Number(empId);
        });
    }, [users, roleId, empId, isSuper, isManager, teamMemberIds]);

    useEffect(() => {
        fetchLeads();
        fetchUsers();
    }, [roleId, empId, activeOrgId]);

    // Helper to compute default half-year dates based on current month
    const defaultHalfYear = useMemo(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        return month >= 6
            ? { start: `${year}-07-01`, end: `${year}-12-31` }
            : { start: `${year}-01-01`, end: `${year}-06-30` };
    }, []);

    // Filter, Search, Sort, Pagination States
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('ALL'); // ALL, Organization, Individual
    const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, Lead, Customer, Dead
    const [priorityFilter, setPriorityFilter] = useState('ALL'); // ALL, High, Medium, Low
    const [sortField, setSortField] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');
    const [entries, setEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Modal States
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [viewLead, setViewLead] = useState(null);
    const [editingId, setEditingId] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    // Form State
    const [form, setForm] = useState({
        AccountType: 'Organization',
        AccountName: '',
        ContactPerson: '',
        ContactDesignation: '',
        CntPhone1: '',
        CntMail1: '',
        CntPhone2: '',
        CntMail2: '',
        City: '',
        Linkedin: '',
        Status: 'Lead',
        Priority: 'Medium',
        LeadGenerator: empId || '',
        Responsible: empId || '',
        AssignedTo: empId || '',
        Notes: ''
    });

    const openCreate = (defaultType = 'Organization') => {
        setIsViewMode(false);
        setIsEditMode(false);
        setEditingId(null);
        setViewLead(null);
        setForm({
            AccountType: defaultType,
            AccountName: '',
            ContactPerson: '',
            ContactDesignation: '',
            CntPhone1: '',
            CntMail1: '',
            CntPhone2: '',
            CntMail2: '',
            City: '',
            Linkedin: '',
            Status: 'Lead',
            Priority: 'Medium',
            LeadGenerator: empId || '',
            Responsible: empId || '',
            AssignedTo: empId || '',
            Notes: ''
        });
        setShowModal(true);
    };

    const handleEdit = (lead) => {
        setIsViewMode(false);
        setIsEditMode(true);
        setEditingId(lead.id);
        setViewLead(lead);
        setForm({
            AccountType: lead.type || 'Organization',
            AccountName: lead.name || '',
            ContactPerson: lead.contactPerson || '',
            ContactDesignation: lead.designation || '',
            CntPhone1: lead.phone || '',
            CntMail1: lead.email || '',
            CntPhone2: lead.phone2 || '',
            CntMail2: lead.email2 || '',
            City: lead.city || '',
            Linkedin: lead.linkedin || '',
            Status: lead.status || 'Lead',
            Priority: lead.priority || 'Medium',
            LeadGenerator: lead.leadGenerator || lead.assignedTo || empId || '',
            Responsible: lead.responsible || lead.assignedTo || empId || '',
            AssignedTo: lead.assignedTo || empId || '',
            Notes: lead.notes || ''
        });
        setShowModal(true);
    };

    const openView = (lead) => {
        setIsViewMode(true);
        setIsEditMode(false);
        setShowModal(false);
        setEditingId(lead.id);
        setViewLead(lead);
    };

    const openDelete = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/leads/${deleteId}`);
            setLeads(prev => prev.filter(l => l.id !== deleteId));
            setShowDeleteModal(false);
            setDeleteId(null);
        } catch (err) {
            console.error('Delete error:', err);
            alert('Failed to delete account');
        }
    };

    const handleDownloadTemplate = () => {
        const templateRows = [
            {
                'AccountName': 'Apex Biotech Labs',
                'AccountType': 'Organization',
                'ContactPerson': 'Dr. Sarah Chen',
                'ContactDesignation': 'Head of Research',
                'CntPhone1': '+1 555 123 4567',
                'CntMail1': 'sarah@apex.com',
                'CntPhone2': '',
                'CntMail2': '',
                'City': 'Lahore',
                'Linkedin': 'https://www.linkedin.com/company/apex-biotech',
                'Status': 'Lead',
                'Priority': 'High',
                'Notes': 'Met at MedTech expo'
            },
            {
                'AccountName': 'Dr. Amir Jamal',
                'AccountType': 'Individual',
                'ContactPerson': '',
                'ContactDesignation': 'Consultant Physician',
                'CntPhone1': '+1 555 987 6543',
                'CntMail1': 'amir.jamal@clinic.org',
                'CntPhone2': '',
                'CntMail2': '',
                'City': 'Karachi',
                'Linkedin': 'https://www.linkedin.com/in/amirjamal',
                'Status': 'Lead',
                'Priority': 'Medium',
                'Notes': 'Clinical trial inquiry'
            }
        ];

        const ws = XLSX.utils.json_to_sheet(templateRows);
        ws['!cols'] = [
            { wch: 25 },
            { wch: 15 },
            { wch: 20 },
            { wch: 22 },
            { wch: 18 },
            { wch: 26 },
            { wch: 18 },
            { wch: 26 },
            { wch: 14 },
            { wch: 38 },
            { wch: 12 },
            { wch: 12 },
            { wch: 30 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Leads Import');
        XLSX.writeFile(wb, 'SalesVault_Leads_Import_Template.xlsx');
    };

    const getPreviewVal = (r, ...keys) => {
        if (!r) return '';
        const lowerMap = {};
        Object.keys(r).forEach(k => {
            lowerMap[k.toLowerCase().replace(/[^a-z0-9]/g, '')] = r[k];
        });
        for (const key of keys) {
            const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (lowerMap[normalized] !== undefined && lowerMap[normalized] !== null && String(lowerMap[normalized]).trim() !== '') {
                return String(lowerMap[normalized]).trim();
            }
        }
        return '';
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImportFile(file);
        setImportResult(null);
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const wb = XLSX.read(data, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
                setImportPreview(json);
            } catch (err) {
                console.error('Error parsing xlsx:', err);
                alert('Failed to parse XLSX file.');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleConfirmImport = async () => {
        if (!importFile) return;
        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            const res = await api.post(`/leads/import?empId=${empId}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setImportResult(res.data);
            fetchLeads();
        } catch (err) {
            console.error('Import error:', err);
            alert(err.response?.data?.error || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    // Fast status update with confirmation
    const requestAccountStatusChange = (lead, newStatus) => {
        if (lead.status === newStatus) return;
        setConfirmModal({
            open: true,
            title: 'Confirm Account Status Change',
            message: `Are you sure you want to change "${lead.name}" from "${lead.status}" to "${newStatus}"?`,
            onConfirm: async () => {
                try {
                    await api.patch(`/leads/${lead.id}/status`, { Status: newStatus });
                    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, status: newStatus } : l));
                } catch (err) {
                    console.error("Status update error:", err);
                    alert("Failed to update status");
                }
            }
        });
    };

    // Fast priority update with confirmation
    const requestPriorityChange = (lead, newPriority) => {
        if (lead.priority === newPriority) return;
        setConfirmModal({
            open: true,
            title: 'Confirm Priority Change',
            message: `Are you sure you want to update priority for "${lead.name}" to "${newPriority}"?`,
            onConfirm: async () => {
                try {
                    await api.patch(`/leads/${lead.id}/status`, { Priority: newPriority });
                    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, priority: newPriority } : l));
                } catch (err) {
                    console.error("Priority update error:", err);
                    alert("Failed to update priority");
                }
            }
        });
    };

    const handleSavePrompt = () => {
        if (!form.AccountName.trim()) {
            alert('Account Name is required');
            return;
        }

        setConfirmModal({
            open: true,
            title: isEditMode ? 'Confirm Account Update' : 'Confirm Create Account',
            message: isEditMode
                ? `Are you sure you want to save changes for "${form.AccountName}" (Status: ${form.Status}, Priority: ${form.Priority})?`
                : `Are you sure you want to create "${form.AccountName}" (${form.AccountType})?`,
            onConfirm: async () => {
                try {
                    const payload = {
                        ...form,
                        AssignedTo: form.Responsible || form.AssignedTo || empId,
                        LeadGenerator: form.LeadGenerator || empId,
                        Responsible: form.Responsible || empId
                    };
                    if (isEditMode) {
                        await api.put(`/leads/${editingId}`, payload);
                    } else {
                        await api.post('/leads', payload);
                    }
                    setShowModal(false);
                    fetchLeads();
                } catch (err) {
                    console.error('Save error:', err);
                    alert('Failed to save account');
                }
            }
        });
    };

    // Filter Logic
    const filteredData = useMemo(() => {
        return leads.filter(lead => {
            // Date Filter (Start and End Date)
            if (lead.createdAt) {
                const created = lead.createdAt.slice(0, 10);
                if (startDate && created < startDate) return false;
                if (endDate && created > endDate) return false;
            }

            // Type filter
            if (typeFilter !== 'ALL' && lead.type !== typeFilter) {
                return false;
            }

            // Status filter (Lead vs Customer vs Dead)
            if (statusFilter !== 'ALL' && lead.status !== statusFilter) {
                return false;
            }

            // Priority filter (High, Medium, Low)
            if (priorityFilter !== 'ALL' && lead.priority !== priorityFilter) {
                return false;
            }

            // Search filter
            if (search.trim()) {
                const q = search.toLowerCase();
                const matchId = String(lead.id).includes(q);
                const matchName = lead.name.toLowerCase().includes(q);
                const matchContact = lead.contactPerson.toLowerCase().includes(q);
                const matchDesig = lead.designation.toLowerCase().includes(q);
                const matchPhone = lead.phone.toLowerCase().includes(q);
                const matchEmail = lead.email.toLowerCase().includes(q);
                const matchLinkedin = (lead.linkedin || '').toLowerCase().includes(q);
                const matchCity = lead.city.toLowerCase().includes(q);
                const matchStatus = lead.status.toLowerCase().includes(q);
                const matchPriority = (lead.priority || '').toLowerCase().includes(q);

                if (!matchId && !matchName && !matchContact && !matchDesig && !matchPhone && !matchEmail && !matchLinkedin && !matchCity && !matchStatus && !matchPriority) {
                    return false;
                }
            }

            return true;
        });
    }, [leads, typeFilter, statusFilter, priorityFilter, search]);

    // Sort Logic
    const sortedData = useMemo(() => {
        if (!sortField) return filteredData;

        return [...filteredData].sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            if (sortField === 'id') {
                valA = Number(valA) || 0;
                valB = Number(valB) || 0;
                return sortOrder === 'asc' ? valA - valB : valB - valA;
            }

            valA = String(valA || '').toLowerCase();
            valB = String(valB || '').toLowerCase();

            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortField, sortOrder]);

    // Pagination
    const totalPages = Math.ceil(sortedData.length / entries);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * entries;
        return sortedData.slice(start, start + entries);
    }, [sortedData, currentPage, entries]);

    return (
        <Layout>
            <div className="dome-dashboard-wrap">

                {/* 1. TOP HEADER */}
                <div className="dome-dash-header-row">
                    <div className="dome-dash-title-wrap">
                        <h1 className="dome-dash-h1">Accounts &amp; Leads</h1>
                    </div>
                    <div className="dome-dash-actions">
                        <button
                            type="button"
                            className="btn-dome-secondary"
                            title="Refresh accounts data"
                            onClick={() => fetchLeads()}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                            <span>Refresh</span>
                        </button>

                        <button
                            type="button"
                            className="btn-dome-secondary"
                            title="Download Excel Import Template"
                            onClick={handleDownloadTemplate}
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            <span>Template</span>
                        </button>

                        <button
                            type="button"
                            className={`btn-dome-secondary ${canWrite ? '' : 'btn-action-disabled'}`}
                            title={!canWrite ? 'Select a specific organization to import leads (disabled in All Organizations mode)' : 'Import Leads from XLSX'}
                            onClick={() => { setImportFile(null); setImportPreview([]); setImportResult(null); setImportModal(true); }}
                            disabled={!canWrite}
                        >
                            <FontAwesomeIcon icon={faFileImport} />
                            <span>Import</span>
                        </button>

                        <button
                            type="button"
                            className={`btn-dome-new ${canWrite ? '' : 'btn-action-disabled'}`}
                            onClick={() => openCreate('Organization')}
                            disabled={!canWrite}
                            title={!canWrite ? 'Select a specific organization to create accounts (disabled in All Organizations mode)' : 'Create New Account'}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <span>Create Account</span>
                        </button>
                    </div>
                </div>

                {/* 2. COMPACT FILTER TOOLBAR STRIP */}
                <div className="dome-dash-subnav-strip dome-leads-subnav">
                    <div className="dome-leads-toolbar-left">
                        <div className="dome-period-pills">
                            <button
                                type="button"
                                className={`dome-period-btn ${typeFilter === 'ALL' ? 'active' : ''}`}
                                onClick={() => { setTypeFilter('ALL'); setCurrentPage(1); }}
                            >
                                <FontAwesomeIcon icon={faLayerGroup} className="icon-mr-4" />
                                All ({leads.length})
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${typeFilter === 'Organization' ? 'active' : ''}`}
                                onClick={() => { setTypeFilter('Organization'); setCurrentPage(1); }}
                            >
                                <FontAwesomeIcon icon={faBuilding} className="icon-mr-4" />
                                Organizations ({leads.filter(l => l.type === 'Organization').length})
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${typeFilter === 'Individual' ? 'active' : ''}`}
                                onClick={() => { setTypeFilter('Individual'); setCurrentPage(1); }}
                            >
                                <FontAwesomeIcon icon={faUser} className="icon-mr-4" />
                                Individuals ({leads.filter(l => l.type === 'Individual').length})
                            </button>
                        </div>

                        <div className="dome-search-mini-box">
                            <FontAwesomeIcon icon={faSearch} className="dome-search-mini-icon" />
                            <input
                                type="text"
                                placeholder="Search accounts, emails..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="dome-search-mini-input"
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="dome-search-clear-mini"
                                    onClick={() => { setSearch(''); setCurrentPage(1); }}
                                    title="Clear search"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="dome-leads-toolbar-right">
                        <div className="dome-mini-filter-item">
                            <span className="dome-filter-mini-label">Status:</span>
                            <select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-mini-select"
                            >
                                <option value="ALL">All</option>
                                <option value="Lead">Lead</option>
                                <option value="Customer">Customer</option>
                                <option value="Dead">Dead</option>
                            </select>
                        </div>

                        <div className="dome-mini-filter-item">
                            <span className="dome-filter-mini-label">Priority:</span>
                            <select
                                value={priorityFilter}
                                onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-mini-select"
                            >
                                <option value="ALL">All</option>
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                            </select>
                        </div>

                        <div className="dome-mini-filter-item">
                            <FontAwesomeIcon icon={faCalendarAlt} className="dome-filter-mini-label" />
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                                className="dome-date-mini-input"
                                title="Start date"
                            />
                            <span className="dome-date-sep">&ndash;</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                                className="dome-date-mini-input"
                                title="End date"
                            />
                        </div>

                        {(startDate || endDate || typeFilter !== 'ALL' || statusFilter !== 'ALL' || priorityFilter !== 'ALL' || search) && (
                            <button
                                type="button"
                                className="dome-clear-filters-mini"
                                onClick={() => {
                                    setStartDate('');
                                    setEndDate('');
                                    setTypeFilter('ALL');
                                    setStatusFilter('ALL');
                                    setPriorityFilter('ALL');
                                    setSearch('');
                                    setCurrentPage(1);
                                }}
                                title="Reset all active filters"
                            >
                                <FontAwesomeIcon icon={faTimesCircle} />
                                <span>Reset</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* 3. DOME CARD TABLE */}
                <div className="dome-dash-card">
                    <div className="dome-dash-card-header">
                        <h3 className="dome-dash-card-title">Account Directory</h3>
                        <div className="dome-table-header-controls">
                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Sort:</span>
                                <select
                                    value={sortField}
                                    onChange={(e) => setSortField(e.target.value)}
                                    className="dome-mini-select"
                                >
                                    <option value="id">ID</option>
                                    <option value="name">Account Name</option>
                                    <option value="contactPerson">Contact Person</option>
                                    <option value="city">City</option>
                                    <option value="priority">Priority</option>
                                    <option value="status">Status</option>
                                </select>
                                <button
                                    type="button"
                                    className="dome-sort-dir-mini"
                                    onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                                    title={`Sort Order: ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
                                >
                                    <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountDown : faSortAmountUp} />
                                </button>
                            </div>

                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Show:</span>
                                <select
                                    value={entries}
                                    onChange={(e) => {
                                        setEntries(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="dome-mini-select"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="dome-dash-table-wrap">
                        <table className="dome-dash-table dome-leads-table">
                            <thead>
                                <tr>
                                    <th className="th-id">ID</th>
                                    <th>Account Name</th>
                                    <th>Type</th>
                                    <th>Priority</th>
                                    <th>Account Status</th>
                                    <th>Approach Status</th>
                                    <th>Lead Generator</th>
                                    <th>Responsible</th>
                                    <th className="text-right">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="9" className="td-empty text-center">
                                            Loading accounts...
                                        </td>
                                    </tr>
                                ) : paginatedData.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="td-empty text-center">
                                            No accounts found
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedData.map((lead) => (
                                        <tr
                                            key={lead.id}
                                            className="clickable-row"
                                            onClick={() => openView(lead)}
                                            title="Click to view full account details"
                                        >
                                            <td>
                                                <span className="dome-id-pill">{lead.id}</span>
                                            </td>

                                            <td>
                                                <div className="dome-lead-name-col">
                                                    <span className="dome-lead-name-link">{lead.name}</span>
                                                </div>
                                            </td>

                                            <td>
                                                {lead.type === 'Organization' ? (
                                                    <span>
                                                        Organization
                                                    </span>
                                                ) : (
                                                    <span>
                                                        Individual
                                                    </span>
                                                )}
                                            </td>

                                            {/* Priority Badge with quick change */}
                                            <td>
                                                <select
                                                    value={lead.priority || 'Medium'}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => requestPriorityChange(lead, e.target.value)}
                                                    className={`dome-badge-select dome-priority-${(lead.priority || 'medium').toLowerCase()}`}
                                                >
                                                    <option value="High">High</option>
                                                    <option value="Medium">Medium</option>
                                                    <option value="Low">Low</option>
                                                </select>
                                            </td>

                                            {/* Account Status Toggle (Lead vs Customer vs Dead) */}
                                            <td>
                                                <select
                                                    value={lead.status || 'Lead'}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => requestAccountStatusChange(lead, e.target.value)}
                                                    className={`dome-badge-select dome-status-${(lead.status || 'lead').toLowerCase()}`}
                                                >
                                                    <option value="Lead">Lead</option>
                                                    <option value="Customer">Customer</option>
                                                    <option value="Dead">Dead</option>
                                                </select>
                                            </td>

                                            {/* Approach / Outreach Status Badge */}
                                            <td>
                                                {lead.approachStatus === 'Contacted' ? (
                                                    <span className="badge-status badge-paid">
                                                        <span className="dome-dot dot-paid"></span> Contacted
                                                    </span>
                                                ) : lead.approachStatus === 'Opportunity Created' ? (
                                                    <span className="badge-status badge-submitted">
                                                        <span className="dome-dot dot-submitted"></span> Opp Created
                                                    </span>
                                                ) : lead.approachStatus === 'Rejected' ? (
                                                    <span className="badge-status badge-rejected">
                                                        <span className="dome-dot dot-rejected"></span> Rejected
                                                    </span>
                                                ) : (
                                                    <span className="badge-status badge-neutral">
                                                        <span className="dome-dot dot-neutral"></span> New Lead
                                                    </span>
                                                )}
                                            </td>

                                            <td>
                                                <span>
                                                    {lead.leadGeneratorName || lead.assignedToName || '—'}
                                                </span>
                                            </td>

                                            <td>
                                                <span>
                                                    <strong>{lead.responsibleName || lead.assignedToName || '—'}</strong>
                                                </span>
                                            </td>

                                            <td>
                                                <div className="dome-row-actions" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        type="button"
                                                        className="dome-icon-btn"
                                                        title="Edit Account Details"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEdit(lead);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faPen} />
                                                    </button>
                                                    {canDelete && (
                                                        <button
                                                            type="button"
                                                            className="dome-icon-btn dome-icon-btn-danger"
                                                            title="Delete Account"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openDelete(lead.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* DOME Table Footer Pagination */}
                    <div className="dome-table-footer">
                        <span className="dome-table-counter">
                            Showing {paginatedData.length === 0 ? 0 : (currentPage - 1) * entries + 1}&ndash;{Math.min(currentPage * entries, sortedData.length)} of {sortedData.length} entries
                        </span>

                        <div className="dome-pagination-controls">
                            <button
                                type="button"
                                className="dome-page-btn"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                Prev
                            </button>

                            <span className="dome-page-indicator">Page {currentPage} of {totalPages || 1}</span>

                            <button
                                type="button"
                                className="dome-page-btn"
                                disabled={currentPage === totalPages || totalPages === 0}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                {/* ================= SalesVault LIGHTNING READ VIEW MODAL ================= */}
                <AccountDetailModal
                    isOpen={isViewMode}
                    account={viewLead}
                    users={users}
                    onClose={() => setIsViewMode(false)}
                    onEdit={(lead) => handleEdit(lead)}
                />

                {/* ================= CREATE / EDIT MODAL ================= */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header modal-header-flex">
                                <h3>{isEditMode ? `Edit Account (#${editingId})` : 'New Account / Lead'}</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="modal-btn-close"
                                    title="Close Modal"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <div className="modal-body">
                                {/* Type Switcher: Segmented Toggle UI */}
                                <div className="account-type-toggle-group mb-12">
                                    <div className="slds-segmented-toggle">
                                        <button
                                            type="button"
                                            className={`slds-toggle-option ${form.AccountType === 'Organization' ? 'active' : ''}`}
                                            onClick={() => setForm(f => ({ ...f, AccountType: 'Organization' }))}
                                        >
                                            <FontAwesomeIcon icon={faBuilding} className="toggle-icon" />
                                            <span>Organization / Company</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`slds-toggle-option ${form.AccountType === 'Individual' ? 'active' : ''}`}
                                            onClick={() => setForm(f => ({ ...f, AccountType: 'Individual' }))}
                                        >
                                            <FontAwesomeIcon icon={faUser} className="toggle-icon" />
                                            <span>Individual / Practitioner</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="form-grid-3col">
                                    {/* Row 1: Name & City */}
                                    <div className="input-group input-group-span-2">
                                        <label>
                                            {form.AccountType === 'Organization' ? 'Company / Organization Name' : 'Full Name'} <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            name="AccountName"
                                            value={form.AccountName}
                                            onChange={handleChange}
                                            placeholder={form.AccountType === 'Organization' ? 'e.g. BioPicta Inc, Apex Robotics' : 'e.g. Dr. Amir Jamal'}
                                            required
                                        />
                                    </div>

                                    <div className="input-group">
                                        <label>City / Location</label>
                                        <input
                                            name="City"
                                            value={form.City}
                                            onChange={handleChange}
                                            placeholder="e.g. New York, Lahore"
                                        />
                                    </div>

                                    {/* Row 2: Type Specific Fields & Status */}
                                    {form.AccountType === 'Organization' ? (
                                        <>
                                            <div className="input-group">
                                                <label>Contact Person (Representative)</label>
                                                <input
                                                    name="ContactPerson"
                                                    value={form.ContactPerson}
                                                    onChange={handleChange}
                                                    placeholder="e.g. Dr. Sarah Chen"
                                                />
                                            </div>

                                            <div className="input-group">
                                                <label>Representative Role / Title</label>
                                                <input
                                                    name="ContactDesignation"
                                                    value={form.ContactDesignation}
                                                    onChange={handleChange}
                                                    placeholder="e.g. Chief Medical Officer, CTO"
                                                />
                                            </div>

                                            <div className="input-group">
                                                <label>Account Status</label>
                                                <select
                                                    name="Status"
                                                    value={form.Status}
                                                    onChange={handleChange}
                                                >
                                                    <option value="Lead">Lead</option>
                                                    <option value="Customer">Customer</option>
                                                </select>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="input-group input-group-span-2">
                                                <label>Profession / Specialization</label>
                                                <input
                                                    name="ContactDesignation"
                                                    value={form.ContactDesignation}
                                                    onChange={handleChange}
                                                    placeholder="e.g. Consultant Physician, Specialist"
                                                />
                                            </div>

                                            <div className="input-group">
                                                <label>Account Status</label>
                                                <select
                                                    name="Status"
                                                    value={form.Status}
                                                    onChange={handleChange}
                                                >
                                                    <option value="Lead">Lead</option>
                                                    <option value="Customer">Customer</option>
                                                </select>
                                            </div>
                                        </>
                                    )}

                                    {/* Row 3: Phones & Priority */}
                                    <div className="input-group">
                                        <label>Primary Phone</label>
                                        <input
                                            name="CntPhone1"
                                            value={form.CntPhone1}
                                            onChange={handleChange}
                                            placeholder="e.g. +1 555 123 4567"
                                        />
                                    </div>

                                    <div className="input-group">
                                        <label>Secondary Phone</label>
                                        <input
                                            name="CntPhone2"
                                            value={form.CntPhone2}
                                            onChange={handleChange}
                                            placeholder="e.g. +1 555 987 6543"
                                        />
                                    </div>

                                    <div className="input-group">
                                        <label>Account Priority</label>
                                        <select
                                            name="Priority"
                                            value={form.Priority}
                                            onChange={handleChange}
                                        >
                                            <option value="High">High (Immediate Outreach)</option>
                                            <option value="Medium">Medium (Standard Pipeline)</option>
                                            <option value="Low">Low (General Lead)</option>
                                        </select>
                                    </div>

                                    {/* Row 4: Emails & LinkedIn */}
                                    <div className="input-group">
                                        <label>Primary Email</label>
                                        <input
                                            type="email"
                                            name="CntMail1"
                                            value={form.CntMail1}
                                            onChange={handleChange}
                                            placeholder="e.g. contact@company.com"
                                        />
                                    </div>

                                    <div className="input-group">
                                        <label>Secondary Email</label>
                                        <input
                                            type="email"
                                            name="CntMail2"
                                            value={form.CntMail2}
                                            onChange={handleChange}
                                            placeholder="e.g. admin@company.com"
                                        />
                                    </div>

                                    <div className="input-group">
                                        <label>LinkedIn Profile / Company URL</label>
                                        <input
                                            type="text"
                                            name="Linkedin"
                                            value={form.Linkedin}
                                            onChange={handleChange}
                                            placeholder="e.g. linkedin.com/in/..."
                                        />
                                    </div>

                                    {/* Row 5: Lead Generator & Responsible */}
                                    <div className="input-group">
                                        <label>Lead Generator (Entered By)</label>
                                        <select
                                            name="LeadGenerator"
                                            value={form.LeadGenerator}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Team Member ({assignableLeadMembers.length})...</option>
                                            {assignableLeadMembers.map(u => (
                                                <option key={u.empId} value={u.empId}>{u.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="input-group input-group-span-2">
                                        <label>Responsible (Reaching Out)</label>
                                        <select
                                            name="Responsible"
                                            value={form.Responsible}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Responsible Person ({assignableLeadMembers.length})...</option>
                                            {assignableLeadMembers.map(u => (
                                                <option key={u.empId} value={u.empId}>{u.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Row 6: Notes (Full Width) */}
                                    <div className="input-group input-group-full">
                                        <label>Account Notes &amp; Requirement Summary</label>
                                        <textarea
                                            name="Notes"
                                            value={form.Notes}
                                            onChange={handleChange}
                                            placeholder="Enter requirements, clinical details, or inquiry notes..."
                                            rows={2}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn-dome-secondary"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn-dome-new"
                                    onClick={handleSavePrompt}
                                >
                                    {isEditMode ? 'Save Account Changes' : 'Create Account'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ================= CONFIRMATION MODAL ================= */}
                {confirmModal.open && (
                    <div className="modal-overlay">
                        <div className="modal modal-confirm-sm">
                            <div className="modal-header">
                                <h3>{confirmModal.title}</h3>
                            </div>
                            <div className="modal-body">
                                <p className="modal-confirm-msg">
                                    {confirmModal.message}
                                </p>
                                <div className="modal-footer modal-footer-mt-20">
                                    <button
                                        type="button"
                                        className="btn-dome-secondary"
                                        onClick={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-dome-new"
                                        onClick={() => {
                                            if (typeof confirmModal.onConfirm === 'function') {
                                                confirmModal.onConfirm();
                                            }
                                            setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
                                        }}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ================= DELETE CONFIRMATION MODAL ================= */}
                {showDeleteModal && (
                    <div className="modal-overlay">
                        <div className="modal">
                            <div className="modal-header">
                                <h3>Confirm Delete</h3>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this account? Associated data might be affected.</p>
                                <div className="modal-footer">
                                    <button
                                        className="btn-dome-secondary"
                                        onClick={() => setShowDeleteModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn-dome-new"
                                        onClick={handleDelete}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ================= XLSX IMPORT MODAL ================= */}
                {importModal && (
                    <div className="modal-overlay">
                        <div className="modal modal-import-xlsx">
                            <div className="modal-header">
                                <h3>Import Leads from XLSX</h3>
                            </div>
                            <div className="modal-body">
                                <div className="mb-16">
                                    <div className="flex-between-center mb-10">
                                        <p className="preview-lead-contact">
                                            Upload a <code>.xlsx</code> file containing leads. Make sure your file follows the standard template format.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleDownloadTemplate}
                                            className="import-template-btn"
                                        >
                                            <FontAwesomeIcon icon={faDownload} /> Download Template
                                        </button>
                                    </div>
                                    <div
                                        className="import-dropzone"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <FontAwesomeIcon icon={faUpload} className="import-drop-icon" />
                                        <p className="import-drop-title">
                                            {importFile ? importFile.name : 'Click to browse or drop .xlsx file here'}
                                        </p>
                                        <small className="import-drop-sub">
                                            {importFile ? `${(importFile.size / 1024).toFixed(1)} KB` : 'Supports Excel (.xlsx, .xls) up to 10MB'}
                                        </small>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept=".xlsx, .xls"
                                            className="d-none"
                                            onChange={handleFileSelect}
                                        />
                                    </div>
                                </div>

                                {importResult && (
                                    <div className="import-success-box">
                                        <FontAwesomeIcon icon={faCheck} className="icon-mr-6" />
                                        {importResult.message}
                                    </div>
                                )}

                                {importPreview.length > 0 && (
                                    <div>
                                        <div className="flex-between-center mb-8">
                                            <span className="preview-lead-name">
                                                Preview (Showing first {Math.min(5, importPreview.length)} of {importPreview.length} rows):
                                            </span>
                                        </div>
                                        <div className="preview-scroll-box">
                                            <table className="slds-table table-preview-compact">
                                                <thead>
                                                    <tr>
                                                        <th>Account Name</th>
                                                        <th>Type</th>
                                                        <th>Contact Person</th>
                                                        <th>Phone</th>
                                                        <th>Email</th>
                                                        <th>LinkedIn</th>
                                                        <th>Status</th>
                                                        <th>Priority</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {importPreview.slice(0, 5).map((r, i) => (
                                                        <tr key={i}>
                                                            <td><strong>{getPreviewVal(r, 'AccountName', 'Account Name', 'Company', 'Name') || '—'}</strong></td>
                                                            <td>{getPreviewVal(r, 'AccountType', 'Account Type', 'Type') || 'Organization'}</td>
                                                            <td>{getPreviewVal(r, 'ContactPerson', 'Contact Person', 'Contact') || '—'}</td>
                                                            <td>{getPreviewVal(r, 'CntPhone1', 'Primary Phone', 'Phone') || '—'}</td>
                                                            <td>{getPreviewVal(r, 'CntMail1', 'Primary Email', 'Email') || '—'}</td>
                                                            <td>{getPreviewVal(r, 'Linkedin', 'LinkedIn', 'LinkedIn Profile', 'LinkedIn URL', 'ProfileURL') || '—'}</td>
                                                            <td>{getPreviewVal(r, 'Status', 'Account Status') || 'Lead'}</td>
                                                            <td>{getPreviewVal(r, 'Priority', 'Account Priority') || 'Medium'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                <div className="modal-footer modal-footer-mt-20">
                                    <button
                                        type="button"
                                        className="btn-dome-secondary"
                                        onClick={() => { setImportModal(false); setImportFile(null); setImportPreview([]); setImportResult(null); }}
                                    >
                                        Close
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-dome-new"
                                        disabled={!importFile || importing || (importResult && importResult.count > 0)}
                                        onClick={handleConfirmImport}
                                    >
                                        {importing ? 'Importing...' : `Import ${importPreview.length > 0 ? `${importPreview.length} ` : ''}Leads`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </Layout>
    );
}

export default Leads;